import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, PencilLine, Film } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VIDEO_NARRATION } from "@/data/videoNarration";
import { PronunciationList } from "@/components/PronunciationList";

const Line = ({ id, text, value, onChange }) => {
  const [open, setOpen] = useState(!!value);
  useEffect(() => { if (value) setOpen(true); }, [value]);
  return (
    <li className={`rounded-xl border p-3 ${value ? "border-primary/60 bg-primary/5" : "border-border"}`} data-testid={`narration-line-${id}`}>
      <div className="flex items-start gap-3">
        <p className="flex-1 text-sm leading-relaxed">{text}</p>
        <button type="button" onClick={() => setOpen(!open)} data-testid={`narration-edit-${id}`}
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold hover:border-primary hover:text-primary"><PencilLine size={12} /> {value ? "Change requested" : "Request change"}</button>
      </div>
      {open && <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} maxLength={2000} data-testid={`narration-input-${id}`}
        placeholder="Type the wording you'd like instead…" className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none" />}
    </li>
  );
};

// Admin: read every lesson video's narration and request wording changes.
export default function AdminVideoWording() {
  const { user } = useAuth();
  const [req, setReq] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/narration-requests").then(({ data }) => setReq(data.requests || {})).catch(() => {}); }, []);
  if (user && user.role !== "admin") return <div className="p-10">Admins only.</div>;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/narration-requests", { requests: req }); toast.success(`${data.count} change request${data.count === 1 ? "" : "s"} saved. Ask me to remake the videos when you're ready.`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const count = Object.values(req).filter((v) => v?.trim()).length;
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 py-10">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="wording-back"><ArrowLeft size={14} /> Admin</Link>
        <h1 className="mt-4 font-serif text-4xl">Video wording</h1>
        <p className="mt-2 text-sm text-muted-foreground">Everything the narrator says in each lesson video. Tap "Request change" on any line, type your new wording and save. Then ask for the videos to be remade. {count} change{count === 1 ? "" : "s"} requested.</p>
        <PronunciationList />
        <div className="sticky top-20 z-20 mt-4 rounded-2xl bg-background/90 py-2 backdrop-blur">
          <button onClick={save} disabled={busy} data-testid="wording-save" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy && <Loader2 size={14} className="animate-spin" />} Save change requests</button>
        </div>
        <div className="mt-4 space-y-6">
          {VIDEO_NARRATION.map((v) => (
            <section key={v.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`narration-video-${v.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 font-serif text-xl"><Film size={16} className="text-primary" /> {v.title}</h2>
                <a href={`/media/intros/${v.file}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-primary hover:underline" data-testid={`narration-watch-${v.id}`}>Watch video →</a>
              </div>
              <ol className="mt-3 space-y-2">
                {v.lines.map((t, k) => { const id = `${v.id}:${k}`; return <Line key={id} id={id.replace(":", "-")} text={t} value={req[id] || ""} onChange={(val) => setReq({ ...req, [id]: val })} />; })}
              </ol>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
